import { useLocation } from "react-router-dom";
import { useMemo } from "react";

import useUserStore from "@/features/auth/store/useUserStore.ts";
import { AvailableRoles } from "@/types";
import { useCurrentWorkspaceStore } from "@/features/workspaces/store/useCurrentWorkspaceStore.tsx";

export type UserState =
  | "notAuthorized"
  | "notOnboarded"
  | "tutorWithoutPayments"
  | "tutorWithoutProfile"
  | "tutorWithoutPricing"
  | "tutorWithoutWorkspace"
  | "studentInWorkspace"
  | "tutorInWorkspace"
  | "student"
  | "tutor"
  | "unknown";

const useUserState = (): UserState => {
  const { user } = useUserStore();
  const location = useLocation();
  const { currentWorkspace } = useCurrentWorkspaceStore();

  return useMemo(() => {
    if (!user) {
      return "notAuthorized";
    }
    if (user && !user.isOnboarded) {
      return "notOnboarded";
    }

    if (user.role === AvailableRoles.TUTOR && !user.specialRoleChecks.has_public_profile) {
      return "tutorWithoutProfile";
    }
    if (user.role === AvailableRoles.TUTOR && !user.specialRoleChecks.has_default_pricing) {
      return "tutorWithoutPricing";
    }
    if (user.role === AvailableRoles.TUTOR && !user.specialRoleChecks.has_payments_setup) {
      return "tutorWithoutPayments";
    }
    if (
      user.role === AvailableRoles.TUTOR &&
      localStorage.getItem("should_show_invite_help") === "true"
    ) {
      return "tutorWithoutWorkspace";
    }
    if (user.role === AvailableRoles.STUDENT && currentWorkspace) {
      return "studentInWorkspace";
    }
    if (user.role === AvailableRoles.TUTOR && currentWorkspace) {
      return "tutorInWorkspace";
    }
    if (user.role === AvailableRoles.STUDENT) {
      return "student";
    }
    if (user.role === AvailableRoles.TUTOR) {
      return "tutor";
    }

    return "unknown";
  }, [location.pathname, user, currentWorkspace]);
};

export default useUserState;
